import type { Quiz } from '../types'

const T = { label: 'True', correct: true }
const F = { label: 'False' }
const t = { label: 'True' }
const f = { label: 'False', correct: true }

export const MORE_A: Record<string, Quiz[]> = {
  'factory-method': [
    {
      format: 'judge',
      prompt: 'A function that always returns new RedisStore() is a Factory Method.',
      choices: [t, f],
      explanation: 'With one fixed class there is no choice to hide; it only wraps a constructor.',
    },
    {
      format: 'judge',
      prompt: 'In Factory Method, a subclass can decide which concrete class gets created.',
      choices: [T, F],
      explanation:
        'The base class calls an overridable create method; each subclass returns its own product.',
    },
    {
      format: 'quick_pick',
      prompt: 'Callers need a Mailer without knowing if it is SMTP or SES, picked by config. Which pattern?',
      choices: [
        { label: 'Adapter' },
        { label: 'Prototype' },
        { label: 'Factory Method', correct: true },
        { label: 'Singleton' },
      ],
      explanation: 'One creating method returns the Mailer interface and hides the concrete class.',
    },
    {
      format: 'quick_pick',
      prompt: 'Odd one out: which is NOT a reason to reach for Factory Method?',
      choices: [
        { label: 'Class depends on config' },
        { label: 'Subclasses pick the product' },
        { label: 'Caller should hold an interface' },
        { label: 'Cloning an existing object', correct: true },
      ],
      explanation: 'Copying an existing object is Prototype; the other three describe Factory Method.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `class Invoice
{
    public function total(): float
    {
        return array_sum(
            array_map(fn ($l) => $l->price,
                      $this->lines)
        );
    }
}`,
      choices: [
        { label: 'Factory Method' },
        { label: 'Builder' },
        { label: 'None of these', correct: true },
        { label: 'Misused' },
      ],
      explanation: 'It just sums prices; nothing here creates objects behind an interface.',
    },
  ],
  'abstract-factory': [
    {
      format: 'judge',
      prompt: 'An Abstract Factory returns a matching set of parts, like a grammar and its processor.',
      choices: [T, F],
      explanation: 'Its job is a family of parts that belong together.',
    },
    {
      format: 'judge',
      prompt: 'Abstract Factory is the right fit when each part can vary on its own.',
      choices: [t, f],
      explanation: 'Independent parts need no shared factory; the point is keeping a family matched.',
    },
    {
      format: 'quick_pick',
      prompt: 'A UI kit must give dark or light buttons, inputs and menus that always match. Which pattern?',
      choices: [
        { label: 'Builder' },
        { label: 'Abstract Factory', correct: true },
        { label: 'Bridge' },
        { label: 'Factory Method' },
      ],
      explanation: 'One factory per theme returns every widget in the same style.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Abstract Factory solve?',
      choices: [
        { label: 'Building one object in many steps' },
        { label: 'Sharing one instance everywhere' },
        { label: 'Making an object look different' },
        { label: 'Mixing parts across variants', correct: true },
      ],
      explanation: 'A concrete factory only hands out parts of its own variant.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `class DarkUi implements UiFactory
{
    public function button(): Button
    {
        return new DarkButton;
    }

    public function input(): Input
    {
        return new LightInput;
    }
}`,
      choices: [
        { label: 'Abstract Factory' },
        { label: 'Factory Method' },
        { label: 'None of these' },
        { label: 'Misused', correct: true },
      ],
      explanation: 'A dark kit returns a light input, so the family is mixed.',
    },
  ],
  builder: [
    {
      format: 'judge',
      prompt: 'A Builder lets you set options in any order, then make the object with one final call.',
      choices: [T, F],
      explanation: 'Steps are collected first; the result appears only at the final build call.',
    },
    {
      format: 'judge',
      prompt: 'A Builder is worth adding for a class with two required fields.',
      choices: [t, f],
      explanation: 'Two fields fit a constructor; a builder there is extra ceremony.',
    },
    {
      format: 'quick_pick',
      prompt:
        'An email has optional cc, bcc, files and headers, and callers want readable setup. Which pattern?',
      choices: [
        { label: 'Prototype' },
        { label: 'Factory Method' },
        { label: 'Builder', correct: true },
        { label: 'Abstract Factory' },
      ],
      explanation: 'Chained steps set only what is needed, then one call produces the email.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which is NOT something Builder helps with?',
      choices: [
        { label: 'Many optional settings' },
        { label: 'Keeping one shared instance', correct: true },
        { label: 'Readable call sites' },
        { label: 'One place to validate' },
      ],
      explanation: 'One shared instance is Singleton; the others are what a builder is for.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `cfg := Server{
	Addr:    ":8080",
	Timeout: 5 * time.Second,
	Debug:   false,
}

func Run(cfg Server) error {
	return listen(cfg.Addr)
}`,
      choices: [
        { label: 'Builder' },
        { label: 'Prototype' },
        { label: 'None of these', correct: true },
        { label: 'Misused' },
      ],
      explanation: 'A plain struct literal, no steps and no build call.',
    },
  ],
  prototype: [
    {
      format: 'judge',
      prompt: 'Prototype makes a new object by copying an existing one.',
      choices: [T, F],
      explanation: 'The existing object is the template; the copy is then adjusted.',
    },
    {
      format: 'judge',
      prompt: 'Cloning an object that holds a live DB connection is a safe use of Prototype.',
      choices: [t, f],
      explanation: 'A copy would share or duplicate the handle; such objects should not be cloned.',
    },
    {
      format: 'quick_pick',
      prompt: 'A template record is duplicated and then edited slightly, not built again. Which pattern?',
      choices: [
        { label: 'Builder' },
        { label: 'Factory Method' },
        { label: 'Singleton' },
        { label: 'Prototype', correct: true },
      ],
      explanation: 'Copying a ready object beats rebuilding it from scratch.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Prototype solve?',
      choices: [
        { label: 'Building an object in steps' },
        { label: 'Copying a configured object', correct: true },
        { label: 'Wrapping an incompatible API' },
        { label: 'Choosing a class by config' },
      ],
      explanation: 'It duplicates a configured object without rebuilding it.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `type Doc struct {
	Title string
	Tags  []string
}

func (d Doc) Clone() Doc {
	c := d
	c.Tags = append([]string(nil), d.Tags...)
	return c
}`,
      choices: [
        { label: 'Builder' },
        { label: 'Prototype', correct: true },
        { label: 'None of these' },
        { label: 'Misused' },
      ],
      explanation: 'Clone copies the doc and its tags, so the copy is independent.',
    },
  ],
  singleton: [
    {
      format: 'judge',
      prompt: 'A static getInstance() makes tests harder because the instance cannot be swapped.',
      choices: [T, F],
      explanation: 'Hidden global access cannot be replaced with a fake in a test.',
    },
    {
      format: 'judge',
      prompt: 'A Singleton is a good home for the current user and their request data.',
      choices: [t, f],
      explanation: 'One shared instance can outlive requests, so user data would leak between users.',
    },
    {
      format: 'quick_pick',
      prompt: 'Exactly one connection pool must exist and everything must share it. Which pattern?',
      choices: [
        { label: 'Prototype' },
        { label: 'Factory Method' },
        { label: 'Singleton', correct: true },
        { label: 'Abstract Factory' },
      ],
      explanation: 'A pool must be created once and handed to every user.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Singleton solve?',
      choices: [
        { label: 'Needing one shared instance', correct: true },
        { label: 'Copying objects cheaply' },
        { label: 'Building objects in steps' },
        { label: 'Matching a family of parts' },
      ],
      explanation: 'It guarantees a single instance that all callers share.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `$this->app->singleton(
    Gateway::class,
    fn () => new StripeGateway(
        config('pay.key')
    )
);

$gw = app(Gateway::class);`,
      choices: [
        { label: 'Factory Method' },
        { label: 'Singleton', correct: true },
        { label: 'None of these' },
        { label: 'Misused' },
      ],
      explanation: 'The container builds the gateway once and returns that same instance.',
    },
  ],
  adapter: [
    {
      format: 'judge',
      prompt: 'An Adapter changes the interface of an existing class so your code can call it.',
      choices: [T, F],
      explanation: 'It translates calls from the interface you expect to the one the class has.',
    },
    {
      format: 'quick_pick',
      prompt:
        'Your code expects a PaymentGateway, but the vendor SDK names its methods differently. Which pattern?',
      choices: [
        { label: 'Facade' },
        { label: 'Adapter', correct: true },
        { label: 'Bridge' },
        { label: 'Decorator' },
      ],
      explanation: 'A small class translates your interface to the SDK without changing either side.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Adapter solve?',
      choices: [
        { label: 'Too many subclasses per axis' },
        { label: 'Objects must be built in steps' },
        { label: 'Leaves and groups need one API' },
        { label: 'Two interfaces do not match', correct: true },
      ],
      explanation: 'It lets code use a class whose interface differs from the one it expects.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `type Clock interface{ Now() time.Time }

type sdkClock struct{ c *vendor.Clock }

func (s sdkClock) Now() time.Time {
	return time.Unix(s.c.Secs(), 0)
}`,
      choices: [
        { label: 'Bridge' },
        { label: 'None of these' },
        { label: 'Adapter', correct: true },
        { label: 'Misused' },
      ],
      explanation: 'sdkClock makes the vendor clock fit our Clock interface.',
    },
  ],
  bridge: [
    {
      format: 'judge',
      prompt: 'Bridge keeps an abstraction and its implementation in two separate hierarchies.',
      choices: [T, F],
      explanation: 'The abstraction holds a reference to the implementation and delegates to it.',
    },
    {
      format: 'judge',
      prompt: 'Bridge is the right fix when only one thing varies.',
      choices: [t, f],
      explanation: 'With one axis a plain interface works; Bridge pays off for two independent axes.',
    },
    {
      format: 'quick_pick',
      prompt: 'Reports can go by email, Slack or SMS, and reports and channels change alone. Which pattern?',
      choices: [
        { label: 'Bridge', correct: true },
        { label: 'Abstract Factory' },
        { label: 'Composite' },
        { label: 'Adapter' },
      ],
      explanation: 'Report kinds and channels vary separately, so they are joined by a reference.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Bridge solve?',
      choices: [
        { label: 'Building objects in many steps' },
        { label: 'Matching a family of parts' },
        { label: 'Subclasses multiplying per axis', correct: true },
        { label: 'Sharing one instance' },
      ],
      explanation: 'Two axes as separate hierarchies avoid one subclass per combination.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `abstract class Notice
{
    public function __construct(
        protected Channel $channel
    ) {}

    abstract public function body(): string;

    public function send(): void
    {
        $this->channel->push($this->body());
    }
}`,
      choices: [
        { label: 'Misused' },
        { label: 'Bridge', correct: true },
        { label: 'Adapter' },
        { label: 'None of these' },
      ],
      explanation: 'Notice varies by body and delegates delivery to a swappable Channel.',
    },
  ],
  composite: [
    {
      format: 'judge',
      prompt: 'In a Composite, callers treat a single leaf and a group through the same interface.',
      choices: [T, F],
      explanation: 'That shared interface is what lets callers ignore which node they hold.',
    },
    {
      format: 'judge',
      prompt: 'In a Composite, only the groups need to implement the shared interface.',
      choices: [t, f],
      explanation: 'Leaves and groups both implement it; that is what hides the difference.',
    },
    {
      format: 'quick_pick',
      prompt: 'A folder holds files and other folders, and size() must work on either. Which pattern?',
      choices: [
        { label: 'Bridge' },
        { label: 'Composite', correct: true },
        { label: 'Adapter' },
        { label: 'Prototype' },
      ],
      explanation: 'Files and folders share one interface, and a folder sums its children.',
    },
    {
      format: 'quick_pick',
      prompt: 'Odd one out: three of these are natural Composites.',
      choices: [
        { label: 'Nested comment threads' },
        { label: 'Org chart units' },
        { label: 'A flat price list', correct: true },
        { label: 'Folders of files' },
      ],
      explanation: 'A flat list has no parts inside parts, so there is no tree.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `type Node interface{ Size() int }

type File struct{ n int }

func (f File) Size() int { return f.n }

type Dir struct{ kids []Node }

func (d Dir) Size() int {
	return len(d.kids)
}`,
      choices: [
        { label: 'Composite' },
        { label: 'Adapter' },
        { label: 'None of these' },
        { label: 'Misused', correct: true },
      ],
      explanation: 'Dir counts its children instead of summing their sizes.',
    },
  ],
}
